import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Building2, ArrowRight, CheckCircle, BarChart3, FileText, Shield, Target, Layers, Clock, Euro, TrendingUp, Users, Wrench, ClipboardList, ExternalLink, Home } from 'lucide-react';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const HERO_IMG = 'https://images.unsplash.com/photo-1530877872966-40bb5529f558?w=1200&q=80&auto=format';
const BUILDING_IMG = 'https://images.unsplash.com/photo-1775315078672-1751f2bb432a?w=800&q=80&auto=format';

export default function ErlaeuterungPage() {
  const [data, setData] = useState(null);
  const [sample, setSample] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/public/erlaeuterung`)
      .then(r => r.json())
      .then(d => {
        setData(d);
        if (d.sample_analysis_id) {
          fetch(`${API_URL}/api/public/analysis/${d.sample_analysis_id}`)
            .then(r => r.ok ? r.json() : null)
            .then(s => setSample(s))
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, []);

  if (!data) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
    </div>
  );

  const conditionLabels = { sehr_gut: 'Sehr gut', gut: 'Gut', mittel: 'Mittel', schlecht: 'Schlecht' };

  return (
    <div className="animate-fade-in" data-testid="erlaeuterung-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-slate-900 text-white" data-testid="erlaeuterung-hero">
        <div className="absolute inset-0 opacity-20">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 py-16 md:py-24">
          <Badge className="bg-blue-600/20 text-blue-300 border-blue-500/30 mb-4">WEG-Analysetool</Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mb-4" data-testid="erlaeuterung-title">
            {data.hero_title || 'Das WEG-Analysetool im Detail'}
          </h1>
          <p className="text-base md:text-lg text-slate-300 max-w-2xl leading-relaxed mb-8">
            {data.hero_subtitle || 'Transparente Bedarfsermittlung für die Verwalterbranche'}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/analyse">
              <Button size="lg" className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="erlaeuterung-cta">
                Jetzt Analyse starten <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            {sample && (
              <a href="#musteranalyse">
                <Button size="lg" variant="outline" className="gap-2 border-slate-500 text-slate-200 hover:bg-slate-800">
                  Musteranalyse ansehen <ExternalLink className="h-4 w-4" />
                </Button>
              </a>
            )}
          </div>
        </div>
      </section>

      {/* ÜBER DAS TOOL */}
      <section className="py-16 px-6" data-testid="about-tool">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-3">
              <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest mb-2">Über das Tool</p>
              <h2 className="text-xl md:text-2xl font-bold tracking-tight mb-4">Was ist das WEG-Analysetool?</h2>
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                {data.about_tool}
              </p>
            </div>
            <div className="lg:col-span-2">
              <img src={BUILDING_IMG} alt="Wohngebäude" className="rounded-xl shadow-lg w-full h-56 object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* SO FUNKTIONIERT ES */}
      <section className="py-16 px-6 bg-slate-50 border-t border-b" data-testid="how-it-works">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest mb-2 text-center">Analysemethodik</p>
          <h2 className="text-xl md:text-2xl font-bold tracking-tight mb-3 text-center">So funktioniert die Analyse</h2>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-3xl mx-auto text-center mb-10 whitespace-pre-line">
            {data.how_it_works}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {[
              { icon: Layers, color: 'bg-blue-100 text-blue-600', title: 'Gebäudekomplexität', desc: 'Einheiten, Gebäudeteile, Aufzug, Tiefgarage, Heizung und weitere Ausstattungsmerkmale.' },
              { icon: Wrench, color: 'bg-purple-100 text-purple-600', title: 'Gebäudezustand', desc: 'Baujahr, durchgeführte Modernisierungen, anstehende Sanierungen und Reparaturhistorie.' },
              { icon: ClipboardList, color: 'bg-amber-100 text-amber-600', title: 'Verwaltungshistorie', desc: 'Eigentümerversammlungen, Zahlungsverzug, Gerichtsverfahren, Rücklagenniveau und mehr.' },
            ].map((item, i) => (
              <Card key={i} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className={`h-12 w-12 rounded-xl ${item.color} flex items-center justify-center mx-auto mb-4`}>
                    <item.icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-base font-semibold mb-2">{item.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Scoring Flow */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-sm">
            {['Daten eingeben', 'Scoring berechnen', 'Bedarf ermitteln', 'Honorar empfehlen'].map((step, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="flex items-center gap-2 px-4 py-2 bg-white border rounded-lg shadow-sm">
                  <span className="h-6 w-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                  <span className="text-xs font-medium">{step}</span>
                </div>
                {i < 3 && <ArrowRight className="h-4 w-4 text-slate-300 hidden sm:block" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-16 px-6" data-testid="features">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold tracking-tight mb-8 text-center">{data.features_title || 'Funktionen im Überblick'}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: BarChart3, title: 'Objektive Bewertung', desc: 'Datenbasierter Score aus 20+ Kriterien' },
              { icon: Euro, title: 'Honorarempfehlung', desc: 'Marktgerechte Vergütung nach Einheiten und Bedarf' },
              { icon: FileText, title: 'PDF-Report', desc: 'Professioneller Bericht zum Download und Versand' },
              { icon: Shield, title: 'DSGVO-konform', desc: 'Keine Tracking-Cookies, keine Daten an Dritte' },
              { icon: Clock, title: 'In 5 Minuten', desc: 'Schnelle Analyse ohne Registrierung möglich' },
              { icon: TrendingUp, title: 'Stetige Weiterentwicklung', desc: 'Regelmäßige Updates und neue Funktionen' },
            ].map((f, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-lg border hover:bg-slate-50 transition-colors">
                <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <f.icon className="h-4 w-4 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold">{f.title}</p>
                  <p className="text-xs text-muted-foreground">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MUSTERANALYSE */}
      {sample && (
        <section className="py-16 px-6 bg-slate-900 text-white" id="musteranalyse" data-testid="sample-analysis">
          <div className="max-w-5xl mx-auto">
            <p className="text-xs font-semibold text-blue-400 uppercase tracking-widest mb-2 text-center">Live-Beispiel</p>
            <h2 className="text-xl md:text-2xl font-bold tracking-tight mb-2 text-center">Musteranalyse</h2>
            <p className="text-sm text-slate-400 text-center mb-8 max-w-xl mx-auto">
              Sehen Sie anhand eines realen Beispiels, wie das WEG-Analysetool eine Liegenschaft bewertet und eine Honorarempfehlung ableitet.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Objektdaten */}
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="h-4 w-4 text-blue-400" />
                    <p className="text-sm font-semibold text-white">Objektdaten</p>
                  </div>
                  <div className="space-y-2 text-sm">
                    {sample.name && <div className="flex justify-between"><span className="text-slate-400">Bezeichnung</span><span className="text-white font-medium">{sample.name}</span></div>}
                    <div className="flex justify-between"><span className="text-slate-400">Einheiten</span><span className="text-white font-medium">{sample.units}</span></div>
                    {sample.construction_year && <div className="flex justify-between"><span className="text-slate-400">Baujahr</span><span className="text-white font-medium">{sample.construction_year}</span></div>}
                    {sample.general_condition && <div className="flex justify-between"><span className="text-slate-400">Zustand</span><span className="text-white font-medium">{conditionLabels[sample.general_condition] || sample.general_condition}</span></div>}
                    {sample.city && <div className="flex justify-between"><span className="text-slate-400">Stadt</span><span className="text-white font-medium">{sample.city}</span></div>}
                  </div>
                </CardContent>
              </Card>

              {/* Scores */}
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="h-4 w-4 text-blue-400" />
                    <p className="text-sm font-semibold text-white">Bewertungsergebnis</p>
                  </div>
                  {sample.demand_percentage != null && (
                    <div className="text-center py-3">
                      <div className="text-4xl font-extrabold text-white tabular-nums">{sample.demand_percentage}%</div>
                      <p className="text-xs text-slate-400 mt-1">Verwaltungsbedarf</p>
                      {sample.demand_category && <Badge className="mt-2 bg-slate-700 text-slate-200 border-slate-600">{sample.demand_category}</Badge>}
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-2 text-center">
                    {[
                      { label: 'Komplex.', value: sample.building_complexity_score, color: 'text-blue-400' },
                      { label: 'Zustand', value: sample.building_condition_score, color: 'text-purple-400' },
                      { label: 'Historie', value: sample.history_score, color: 'text-amber-400' },
                    ].map((s, i) => (
                      <div key={i} className="bg-slate-700/50 rounded-lg p-2">
                        <p className={`text-lg font-bold tabular-nums ${s.color}`}>{s.value != null ? Math.round(s.value) : '-'}</p>
                        <p className="text-[10px] text-slate-500">{s.label}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Honorar */}
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Euro className="h-4 w-4 text-blue-400" />
                    <p className="text-sm font-semibold text-white">Honorarempfehlung</p>
                  </div>
                  {sample.recommended_fee != null && (
                    <div className="text-center py-3">
                      <div className="text-4xl font-extrabold text-white tabular-nums">{Math.round(sample.recommended_fee)} <span className="text-lg text-slate-400">EUR</span></div>
                      <p className="text-xs text-slate-400 mt-1">pro Einheit / Monat netto</p>
                    </div>
                  )}
                  {sample.recommended_fee != null && (
                    <div className="grid grid-cols-2 gap-2 text-center text-sm">
                      <div className="bg-slate-700/50 rounded-lg p-2">
                        <p className="text-white font-semibold tabular-nums">{(Math.round(sample.recommended_fee) * (sample.units || 1)).toLocaleString('de-DE')} EUR</p>
                        <p className="text-[10px] text-slate-500">Monatl. netto</p>
                      </div>
                      <div className="bg-slate-700/50 rounded-lg p-2">
                        <p className="text-white font-semibold tabular-nums">{(Math.round(sample.recommended_fee) * (sample.units || 1) * 12).toLocaleString('de-DE')} EUR</p>
                        <p className="text-[10px] text-slate-500">Jährl. netto</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="text-center mt-8">
              <Link to={`/analyse?id=${sample.analysis_id || sample.id}`}>
                <Button className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="view-sample-btn">
                  Vollständige Musteranalyse ansehen <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* BRANCHENBEDEUTUNG */}
      <section className="py-16 px-6" data-testid="industry-value">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
            <div className="lg:col-span-3">
              <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest mb-2">Für die Branche</p>
              <h2 className="text-xl md:text-2xl font-bold tracking-tight mb-4">Bedeutung für die WEG-Verwaltung</h2>
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                {data.industry_value}
              </p>
            </div>
            <div className="lg:col-span-2 space-y-3">
              {[
                { icon: CheckCircle, text: 'Objektive Bewertungsgrundlage' },
                { icon: Users, text: 'Vertrauen zwischen Verwaltern und Eigentümern' },
                { icon: TrendingUp, text: 'Marktgerechte Honorargestaltung' },
                { icon: Shield, text: 'Transparenz und Fairness' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-lg border bg-slate-50">
                  <item.icon className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                  <span className="text-sm font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ÜBER UNS */}
      <section className="py-16 px-6 bg-slate-50 border-t" data-testid="about-company">
        <div className="max-w-5xl mx-auto text-center">
          <div className="h-14 w-14 rounded-xl bg-slate-900 flex items-center justify-center mx-auto mb-4">
            <Building2 className="h-7 w-7 text-white" />
          </div>
          <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest mb-2">Über uns</p>
          <h2 className="text-xl md:text-2xl font-bold tracking-tight mb-4">Scholze-Kurz & Kurz Immobilien GmbH</h2>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-3xl mx-auto whitespace-pre-line">
            {data.about_company}
          </p>
          <div className="flex justify-center mt-6">
            <img src="/zertifizierter_verwalter.png" alt="Zertifizierter WEG Verwalter" className="w-20" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 border-t" data-testid="erlaeuterung-final-cta">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-xl md:text-2xl font-bold tracking-tight mb-3">Jetzt selbst testen</h2>
          <p className="text-sm text-muted-foreground mb-6 max-w-xl mx-auto">
            {data.cta_text || 'Überzeugen Sie sich selbst — starten Sie jetzt Ihre eigene kostenfreie Analyse.'}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/analyse">
              <Button size="lg" className="gap-2 bg-blue-600 hover:bg-blue-700">
                Kostenfreie Analyse starten <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link to="/">
              <Button size="lg" variant="outline" className="gap-2">
                <Home className="h-4 w-4" /> Zur Startseite
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
